<script lang="ts" setup>
  import { IconCheck, IconCopy } from '@tabler/icons-vue';
  import { useId } from 'vue';
  import {
    anchorsMode,
    registerAnchor,
    unregisterAnchor,
  } from '@/featureBadges';
  import { useEnvironmentsStore } from '@/stores/environments';
  import type { FeatureBadgeColor, FeatureBadgeText } from '@/types';
  import { copyToClipboard } from '@/utils/clipboard';
  import { dayjs } from '@/utils/dayjs';
  import type { VFeatureBadgeProps } from './types';

  const COLOR_CLASSES: Record<FeatureBadgeColor, string> = {
    violet: 'text-violet border-violet',
    blue: 'text-blue border-blue',
    green: 'text-green border-green',
    orange: 'text-orange border-orange',
    red: 'text-red border-red',
    inherit: '',
  };

  const BADGE_CLASSES =
    'align-middle border rounded px-[6px] py-1 text-xs font-semibold leading-4 whitespace-nowrap w-fit';

  const props = defineProps<VFeatureBadgeProps>();

  const { locale, t } = useI18n({
    messages: {
      ru: { copy: 'Скопировать имя якоря', copied: 'Скопировано' },
      en: { copy: 'Copy the anchor name', copied: 'Copied' },
    },
  });
  const environmentsStore = useEnvironmentsStore();

  const badge = computed(() => {
    const entry = environmentsStore.FEATURE_BADGES?.[props.anchor];

    if (!entry?.label) {
      return undefined;
    }

    if (entry.until) {
      const until = dayjs(entry.until);

      if (!until.isValid() || !dayjs().isBefore(until)) {
        return undefined;
      }
    }

    return entry;
  });

  const localize = (text: FeatureBadgeText) =>
    text[locale.value as keyof FeatureBadgeText] || text.en;

  const label = computed(() => badge.value && localize(badge.value.label));
  const description = computed(() =>
    badge.value?.description ? localize(badge.value.description) : null,
  );
  const colorClass = computed(
    () => COLOR_CLASSES[badge.value?.color as FeatureBadgeColor] ?? COLOR_CLASSES.blue,
  );

  const claim = (anchor: string) => {
    if (!props.placedByLayer) {
      registerAnchor(anchor);
    }
  };

  const release = (anchor: string) => {
    if (!props.placedByLayer) {
      unregisterAnchor(anchor);
    }
  };

  onMounted(() => claim(props.anchor));
  onUnmounted(() => release(props.anchor));
  watch(
    () => props.anchor,
    (next, previous) => {
      release(previous);
      claim(next);
    },
  );

  const cardTarget = ref<HTMLElement>();
  const card = ref<{ show: () => void; hide: () => void }>();
  const cardText = computed(() =>
    label.value ? (description.value ?? '') : t('copy'),
  );
  const cardTextId = useId();

  const byTap = useMediaQuery('(any-hover: none)');

  const onEnter = () => card.value?.show();
  const onLeave = () => card.value?.hide();

  const onClick = (event: MouseEvent) => {
    if (!byTap.value || !cardText.value) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    card.value?.show();
  };

  const host = computed(
    () => cardTarget.value?.closest('button, a, [tabindex]') ?? null,
  );

  const focusedByKeyboard = (element: Element) => {
    try {
      return element.matches(':focus-visible');
    } catch {
      return true;
    }
  };

  useEventListener(host, 'focusin', (event: FocusEvent) => {
    if (focusedByKeyboard(event.currentTarget as Element)) {
      card.value?.show();
    }
  });

  useEventListener(host, 'focusout', () => card.value?.hide());

  const describes = (element: Element | null, on: boolean) => {
    if (!element) {
      return;
    }

    const ids = (element.getAttribute('aria-describedby') ?? '')
      .split(' ')
      .filter((id) => id && id !== cardTextId);

    if (on) {
      ids.push(cardTextId);
    }

    if (ids.length) {
      element.setAttribute('aria-describedby', ids.join(' '));
    } else {
      element.removeAttribute('aria-describedby');
    }
  };

  watch([host, description], ([element], [previous]) => {
    if (previous && previous !== element) {
      describes(previous, false);
    }

    describes(element, Boolean(description.value));
  });

  onBeforeUnmount(() => describes(host.value, false));

  const copied = ref(false);

  const copyAnchor = () => {
    copyToClipboard(props.anchor);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  };
</script>

<template>
  <span
    v-if="label"
    ref="cardTarget"
    :class="[
      BADGE_CLASSES,
      colorClass,
      'inline-block select-none',
      { 'cursor-help': description },
    ]"
    :aria-describedby="description ? cardTextId : undefined"
    :data-badge-anchor="anchor"
    data-testid="featureBadge"
    @click="onClick"
    @mouseenter="onEnter"
    @mouseleave="onLeave">
    {{ label }}
    <VFeatureBadgeCard
      v-if="description"
      ref="card"
      :target="cardTarget"
      :text="cardText"
      :text-id="cardTextId" />
  </span>
  <button
    v-else-if="anchorsMode"
    ref="cardTarget"
    type="button"
    :class="[
      BADGE_CLASSES,
      'inline-flex items-center gap-4 cursor-pointer transition-colors',
      copied
        ? 'text-green border-green'
        : 'border-dashed text-gray-hover hover:text-black hover:border-black',
    ]"
    :data-badge-anchor="anchor"
    data-testid="featureAnchor"
    @click.stop.prevent="copyAnchor"
    @mouseenter="onEnter"
    @mouseleave="onLeave">
    <IconCheck v-if="copied" class="w-12 h-12" data-testid="anchorCopied" />
    <IconCopy v-else class="w-12 h-12" />
    {{ copied ? t('copied') : anchor }}
    <VFeatureBadgeCard
      ref="card"
      :target="cardTarget"
      :text="cardText"
      :text-id="cardTextId" />
  </button>
  <span v-else hidden :data-badge-anchor="anchor" data-testid="featureAnchor" />
</template>
